import { useState } from 'react'
import { useNavigate } from 'react-router'
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import SmartDisplayIcon from '@mui/icons-material/SmartDisplay'
import DeleteIcon from '@mui/icons-material/Delete'
import { useTranslation } from 'react-i18next'
import type { YouTubeVideo } from '@/types/youtube.types'
import { YouTubeUrlInput } from './YouTubeUrlInput'
import { useDeleteYouTubeVideo } from '@/hooks/useDeleteYouTubeVideo'

interface YouTubeVideoSectionProps {
  /** The project to associate new videos with (passed through to YouTubeUrlInput). */
  projectId: number
  /** Flat list of videos from useYouTubeVideos(); may be empty. */
  videos: YouTubeVideo[]
}

interface ChannelGroup {
  channelId: string
  channelName: string
  channelThumbnailUrl: string | null
  videos: YouTubeVideo[]
}

/**
 * Groups a flat video list into per-channel buckets, preserving insertion order
 * of first-seen channels (backend returns newest-first so most-recently-added
 * channel appears first).
 */
function groupByChannel(videos: YouTubeVideo[]): ChannelGroup[] {
  const map = new Map<string, ChannelGroup>()
  for (const video of videos) {
    const id = video.channel?.id ?? video.channel_id
    if (!map.has(id)) {
      map.set(id, {
        channelId: id,
        channelName: video.channel?.name ?? 'Unknown Channel',
        channelThumbnailUrl: video.channel?.thumbnail_url ?? null,
        videos: [],
      })
    }
    const group = map.get(id)
    if (group) group.videos.push(video)
  }
  return Array.from(map.values())
}

/**
 * YouTubeVideoSection renders the YouTube block inside the project-detail page.
 *
 * - Shows the YouTubeUrlInput add affordance at all times (AC 3: empty-state prompt).
 * - When videos are present, groups them by channel with a channel avatar + name
 *   header per group and a responsive card grid beneath it (AC 1, 2).
 * - Each video card has a delete icon button that opens a confirmation dialog (AC 1).
 * - Confirming the dialog removes the video; cancelling leaves it unchanged (AC 2, 3).
 * - After deletion the user remains on the project page; the refreshed list removes
 *   the card (and channel group when empty) automatically (AC 2).
 * - Uses MUI responsive Grid size objects for mobile-first layout (AC 4).
 * - Does NOT touch the podcast section — that remains unchanged in ProjectDetail.
 */
export const YouTubeVideoSection = ({ projectId, videos }: YouTubeVideoSectionProps) => {
  const { t } = useTranslation('projects')
  const navigate = useNavigate()
  const groups = groupByChannel(videos)

  // Track which video is pending deletion (drives both dialog visibility and mutation).
  const [pendingDeleteVideo, setPendingDeleteVideo] = useState<YouTubeVideo | null>(null)
  const { mutate: deleteVideo, isPending: isDeleting } = useDeleteYouTubeVideo()

  const handleDeleteConfirm = () => {
    if (!pendingDeleteVideo) return
    deleteVideo({ videoId: pendingDeleteVideo.id, projectId }, {
      onSuccess: () => {
        setPendingDeleteVideo(null)
      },
    })
  }

  const handleDeleteCancel = () => {
    if (isDeleting) return // prevent close while request is in-flight
    setPendingDeleteVideo(null)
  }

  const handleOpenPlayer = (video: YouTubeVideo) => {
    navigate(`/projects/${projectId}/videos/${video.id}`)
  }

  return (
    <Box mb={4}>
      {/* Section heading */}
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <SmartDisplayIcon sx={{ color: 'error.main' }} />
        <Typography variant="h5" component="h2">
          {t('youtubeVideos')}
        </Typography>
      </Box>

      {/* Add-video affordance — visible for zero and non-zero states (AC 3) */}
      <Box mb={3}>
        <YouTubeUrlInput projectId={projectId} />
      </Box>

      {/* Zero-state helper copy (AC 3) */}
      {videos.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {t('noYouTubeVideosYet')}
        </Typography>
      )}

      {/* Channel groups (AC 1, 2, 4) */}
      {groups.map((group) => (
        <Box key={group.channelId} mb={4}>
          {/* Channel header row */}
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <Avatar
              src={group.channelThumbnailUrl ?? undefined}
              alt={group.channelName}
              sx={{ width: 32, height: 32 }}
            >
              {group.channelName.charAt(0)}
            </Avatar>
            <Typography variant="h6" component="h3">
              {group.channelName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ({group.videos.length})
            </Typography>
          </Stack>

          {/* Video card grid — mobile-first, no horizontal scroll at 375px (AC 4) */}
          <Grid container spacing={2}>
            {group.videos.map((video) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={video.id}>
                <Card
                  sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative', cursor: 'pointer' }}
                  onClick={() => handleOpenPlayer(video)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleOpenPlayer(video) }}
                  aria-label={t('openVideoPlayer', { title: video.title })}
                >
                  {video.thumbnail_url && (
                    <CardMedia
                      component="img"
                      image={video.thumbnail_url}
                      alt={video.title}
                      sx={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover' }}
                    />
                  )}
                  <CardContent sx={{ flexGrow: 1, pr: 5 }}>
                    <Typography variant="body1" fontWeight="medium">
                      {video.title}
                    </Typography>
                  </CardContent>
                  {/* Delete affordance — positioned top-right so it does not disrupt card layout */}
                  <Tooltip title={t('removeVideo')}>
                    <IconButton
                      aria-label={t('removeVideo')}
                      size="small"
                      onClick={(e) => { e.stopPropagation(); setPendingDeleteVideo(video) }}
                      sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'background.paper', '&:hover': { color: 'error.main' } }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}

      {/* Confirmation dialog — shown when pendingDeleteVideo is set (AC 1, 3) */}
      <Dialog
        open={pendingDeleteVideo !== null}
        onClose={handleDeleteCancel}
        aria-labelledby="remove-video-dialog-title"
        aria-describedby="remove-video-dialog-description"
      >
        <DialogTitle id="remove-video-dialog-title">{t('removeVideoTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText id="remove-video-dialog-description">
            {t('removeVideoMessage', { title: pendingDeleteVideo?.title ?? '' })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          {/* Cancel is listed first and receives default focus (AC 1: first-focus cancel) */}
          <Button onClick={handleDeleteCancel} disabled={isDeleting} autoFocus>
            {t('removeVideoCancel')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            disabled={isDeleting}
            variant="contained"
          >
            {t('removeVideoConfirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
